import { useEffect } from 'react';
import trackingController from '../services/trackingController';
import waypointQueue from '../services/waypointQueue';
import { useAuth } from '../context/AuthContext';

/**
 * Invisible app-level component that resumes GPS tracking state on reload.
 *
 * If the user reloads the page (or re-enters via a different tab) while
 * tracking was active, `trackingController.resumeFromStorage` re-subscribes
 * listeners so points flowing from the native foreground service (or browser
 * watchPosition) continue to be queued.
 *
 * Defensive: only resumes once auth has been determined AND the user is
 * authenticated. If auth resolved as unauthenticated (e.g. session expired
 * across the reload), we hard-halt the tracker so the upload queue does not
 * post 401-spam forever. Queued points are kept, and sent by
 * `resumeAfterSignIn` once somebody signs in again.
 *
 * Mount once at the top level of the app (inside AuthContext).
 */
export default function TrackingWatcher() {
  const { isAuthenticated, isLoading } = useAuth();

  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated) {
      void trackingController.resumeFromStorage();
      void waypointQueue.resumeAfterSignIn();
    } else {
      void trackingController.halt();
    }
  }, [isAuthenticated, isLoading]);

  return null;
}
